import React from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    Collapse,
    Container,
    IconButton,
    LinearProgress,
    Stack,
    Typography,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";

const IncomeMonthsList = ({
    loading,
    err,
    incomeMonthIndexLoaded,
    hasIncomeMonths,
    sortedYears,
    groupedIncomeMonths,
    expandedIncomeGroups,
    loadingIncomeMonths,
    loadedIncomeMonths,
    incomeMonthData,
    buildMonthKey,
    filterAndSortIncomes,
    resolveIncomeDate,
    resolveIncomeCustomerName,
    resolveIncomeDisplayName,
    fmtEUR,
    onToggleYear,
    onToggleMonth,
    onOpenDetails,
    onCopyIncome,
    onOpenReceipt,
    onOpenUpload,
    hasReceipt,
    copyingId,
}) => (
    <Container maxWidth="lg" sx={{ mt: 4, pb: 6 }}>
        {loading && (
            <Box sx={{ mt: 2 }}>
                <LinearProgress />
            </Box>
        )}

        {!loading && err && (
            <Alert severity="error" sx={{ mt: 2 }}>
                {err}
            </Alert>
        )}

        {!loading && !err && incomeMonthIndexLoaded && !hasIncomeMonths && (
            <Card sx={{ borderRadius: 3, boxShadow: "0 6px 24px rgba(0,0,0,0.06)" }}>
                <CardContent>
                    <Typography variant="h5" sx={{ fontWeight: 800 }}>
                        Noch keine Einnahmen vorhanden
                    </Typography>
                    <Typography sx={{ mt: 1.5, opacity: 0.8 }}>
                        Lade deinen ersten Beleg hoch - per Klick oder einfach per Drag & Drop.
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<CloudUploadRoundedIcon />}
                        onClick={onOpenUpload}
                        sx={{ mt: 3, backgroundColor: "orange", color: "#000", fontWeight: 700, "&:hover": { backgroundColor: "#ff9e1a" } }}
                    >
                        Jetzt hochladen
                    </Button>
                </CardContent>
            </Card>
        )}

        {sortedYears.map((year) => {
            const monthsInYear = groupedIncomeMonths[year] || [];

            return (
                <Box key={year} sx={{ mb: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <IconButton
                            size="small"
                            onClick={() => onToggleYear(year)}
                            aria-label={`Jahr ${year} ${
                                expandedIncomeGroups?.years?.[year] ? "einklappen" : "ausklappen"
                            }`}
                        >
                            {expandedIncomeGroups?.years?.[year]
                                ? <ExpandLessRoundedIcon />
                                : <ExpandMoreRoundedIcon />}
                        </IconButton>
                        <Typography variant="h6">{year}</Typography>
                    </Stack>

                    <Collapse in={!!expandedIncomeGroups?.years?.[year]} timeout="auto" unmountOnExit>
                        {monthsInYear.map((monthKey) => {
                            const monthStateKey = buildMonthKey(year, monthKey);
                            const monthList = incomeMonthData[monthStateKey] || [];
                            const isMonthExpanded = !!expandedIncomeGroups?.months?.[monthStateKey];
                            const isLoadingMonth = loadingIncomeMonths.has(monthStateKey);
                            const isMonthLoaded = loadedIncomeMonths.has(monthStateKey);
                            const monthLabel = new Date(Number(year), Number(monthKey), 1).toLocaleDateString("de-DE", {
                                month: "long",
                            });
                            const filteredMonthList = filterAndSortIncomes(monthList);

                            return (
                                <Box key={monthStateKey} sx={{ mb: 2 }}>
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                        <IconButton
                                            size="small"
                                            onClick={() => onToggleMonth(year, monthKey)}
                                            aria-label={`${monthLabel} ${isMonthExpanded ? "einklappen" : "ausklappen"}`}
                                        >
                                            {isMonthExpanded ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                                        </IconButton>
                                        <Typography variant="subtitle1">{monthLabel}</Typography>
                                    </Stack>

                                    <Collapse in={isMonthExpanded} timeout="auto" unmountOnExit>
                                        {isLoadingMonth && (
                                            <Box sx={{ mb: 1 }}>
                                                <LinearProgress />
                                            </Box>
                                        )}
                                        {!isLoadingMonth && isMonthLoaded && filteredMonthList.length === 0 && (
                                            <Typography variant="body2" color="text.secondary">
                                                Keine Einnahmen in diesem Monat.
                                            </Typography>
                                        )}
                                        {filteredMonthList.length > 0 && (
                                            <Grid2 container spacing={2}>
                                                {filteredMonthList.map((income) => {
                                                    const date = resolveIncomeDate(income);
                                                    const customerName = resolveIncomeCustomerName(income);
                                                    const displayName = resolveIncomeDisplayName(income);
                                                    const amountValue = Number.isFinite(Number(income?.amount))
                                                        ? Number(income.amount)
                                                        : null;
                                                    const grossValue = Number.isFinite(Number(income?.grossAmount))
                                                        ? Number(income.grossAmount)
                                                        : null;
                                                    const displayAmount = amountValue ?? grossValue;

                                                    return (
                                                        <Grid2 item key={income.id} xs={12} sm={6} md={4}>
                                                            <Card
                                                                sx={{
                                                                    borderRadius: 3,
                                                                    boxShadow: (theme) =>
                                                                        theme.palette.mode === "dark"
                                                                            ? "0 6px 24px rgba(0,0,0,0.45)"
                                                                            : "0 6px 24px rgba(0,0,0,0.06)",
                                                                    border: (theme) =>
                                                                        `1px solid ${
                                                                            theme.palette.mode === "dark" ? theme.palette.divider : "#ffe0b2"
                                                                        }`,
                                                                    background: (theme) =>
                                                                        theme.palette.mode === "dark"
                                                                            ? theme.palette.background.paper
                                                                            : "linear-gradient(0deg, #fff8e1 0%, #fff 60%)",
                                                                    height: "100%",
                                                                    display: "flex",
                                                                    flexDirection: "column",
                                                                }}
                                                            >
                                                                <CardContent sx={{ flex: 1 }}>
                                                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                                                        <Typography variant="subtitle1" fontWeight={700} sx={{ pr: 1 }}>
                                                                            {displayName}
                                                                        </Typography>
                                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                                            {income?.isEuService && (
                                                                                <Chip size="small" label="EU" color="info" variant="outlined" />
                                                                            )}
                                                                        </Stack>
                                                                    </Stack>
                                                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                                        {date ? new Date(date).toLocaleDateString("de-DE") : "-"}
                                                                    </Typography>

                                                                    {Number.isFinite(displayAmount) && (
                                                                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                                            Betrag: {fmtEUR(displayAmount)}
                                                                        </Typography>
                                                                    )}

                                                                    {!!customerName && (
                                                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                                            Kunde: {customerName}
                                                                        </Typography>
                                                                    )}
                                                                    {income?.taxExcludedContribution && (
                                                                        <Chip
                                                                            size="small"
                                                                            color="success"
                                                                            variant="outlined"
                                                                            label="Einlage (steuerfrei)"
                                                                            sx={{ mt: 1 }}
                                                                        />
                                                                    )}
                                                                </CardContent>

                                                                <CardActions sx={{ pt: 0, pb: 2, px: 2, gap: 1 }}>
                                                                    <Button size="small" variant="outlined" onClick={() => onOpenDetails(income)}>
                                                                        Details
                                                                    </Button>
                                                                    <Button
                                                                        size="small"
                                                                        variant="outlined"
                                                                        onClick={() => onCopyIncome(income)}
                                                                        disabled={copyingId === income.id}
                                                                    >
                                                                        {copyingId === income.id ? "Kopiere..." : "Kopieren"}
                                                                    </Button>
                                                                    <Button
                                                                        size="small"
                                                                        variant="outlined"
                                                                        onClick={() => onOpenReceipt(income)}
                                                                        disabled={!hasReceipt(income)}
                                                                    >
                                                                        Beleg anzeigen
                                                                    </Button>
                                                                </CardActions>
                                                            </Card>
                                                        </Grid2>
                                                    );
                                                })}
                                            </Grid2>
                                        )}
                                    </Collapse>
                                </Box>
                            );
                        })}
                    </Collapse>
                </Box>
            );
        })}
    </Container>
);

export default IncomeMonthsList;
